import {
	CellRendererDataTransformer,
	CustomHeaderCellRenderer,
	TransformedSuperGridColDef,
	getLevelByDepth,
	getLevelDepth,
} from '@aleph/supergrid';
import {
	ColDef, RowClassParams, IRowNode,
} from 'ag-grid-community';
import { SpotifyBreakdowns, SpotifyLevel, SpotifyGroupBreakdowns } from 'wb-spotify/app/routes/superGrid/utils';
import { SPOTIFY_SUPERGRID_CONFIG } from 'wb-spotify/app/routes/superGrid/config';
import {
	AdAccountOrderByColumns, AdOrderByColumn,
	AgeBreakdownSuperGridListDocument, CampaignOrderByColumn,
	CountryBreakdownSuperGridListDocument, DateBreakdownSuperGridListDocument,
	GenderBreakdownSuperGridListDocument, GenreBreakdownSuperGridListDocument,
	GroupOrderByColumn,
	PlatformBreakdownSuperGridListDocument,
	HourBreakdownSuperGridListDocument,
} from 'wb-spotify/graphql/generated';
import { DefaultCellRenderer } from '@aleph/shared-components';

export const getSpotifyBreakdownQuery = (selectedBreakdown?: SpotifyBreakdowns | SpotifyGroupBreakdowns) => {
	if (selectedBreakdown) {
		switch (selectedBreakdown) {
			case SpotifyBreakdowns.DAILY:
				return DateBreakdownSuperGridListDocument;
			case SpotifyGroupBreakdowns.AGE:
				return AgeBreakdownSuperGridListDocument;
			case SpotifyGroupBreakdowns.GENDER:
				return GenderBreakdownSuperGridListDocument;
			case SpotifyGroupBreakdowns.GENRE:
				return GenreBreakdownSuperGridListDocument;
			case SpotifyGroupBreakdowns.COUNTRY:
				return CountryBreakdownSuperGridListDocument;
			case SpotifyGroupBreakdowns.HOURLY:
				return HourBreakdownSuperGridListDocument;
			case SpotifyGroupBreakdowns.PLATFORM:
				return PlatformBreakdownSuperGridListDocument;
		}
	}

	return undefined;
};

export const DEFAULT_SPOTIFY_COL_DEF: ColDef = {
	headerClass:           'border-b border-b-gray-300 border-r border-r-gray-300',
	headerComponent:       CustomHeaderCellRenderer,
	headerComponentParams: params => {
		const level = params?.column?.getColDef()?.level;

		const color = params?.context?.config?.levels?.find(col => col?.level === level)?.anchors?.color;

		return {
			color: color ?? 'hsl(var(--gray-950))',
		};
	},
	cellRenderer:       CellRendererDataTransformer,
	cellRendererParams: {
		originalRenderer: DefaultCellRenderer,
	},
	sortable:  true,
	resizable: true,
	cellStyle: {
		borderRightWidth: 1,
		borderRightColor: 'hsl(var(--gray-300))',
		padding:          0,
	},
	cellClass: params => {
		if ((params?.context?.validations ?? []).some(
			validation => (validation.field === (params?.colDef?.colId ?? params?.colDef?.field))
				&& (
					(validation.id === params?.data?.id && params?.data?.id)
					|| (validation.id === params.data?.hash && params?.data?.hash)
				),
		)) {
			return 'is-invalid';
		}

		return undefined;
	},
};

const isRowStartOfNextLevel = (level: SpotifyLevel) => (params: RowClassParams) => {
	let nextNode: IRowNode | undefined;

	// getDisplayedRowNodeAtIndex is not working properly in the ag grid, so we have to do this
	params.api?.forEachNode((node) => {
		if (node.rowIndex === params.rowIndex + 1) {
			nextNode = node;
		}
	});

	const isNextDeeper = (nextNode?.level ?? -1) > params.node?.level;

	return isNextDeeper && params?.data?.level === level;
};

const isRowEndOfLevel = (level: SpotifyLevel) => (params: RowClassParams) => {
	if (isRowStartOfNextLevel(level)(params)) {
		return false;
	}

	let nextNode: IRowNode | undefined;

	// getDisplayedRowNodeAtIndex is not working properly in the ag grid, so we have to do this
	params.api?.forEachNode((node) => {
		if (node.rowIndex === params.rowIndex + 1) {
			nextNode = node;
		}
	});

	const isLastRowOfLevel = params?.data?.isLastRow;

	if (params?.data?.level === SpotifyLevel.BREAKDOWN) {
		return isLastRowOfLevel;
	}

	return isLastRowOfLevel && params?.data?.level === level && !!nextNode;
};

export const getRedditSuperGridRowClassRules = () => ({
	'big-account': (params: RowClassParams) => params.node?.data?.level === SpotifyLevel.AD_ACCOUNT
			&& !params.node.expanded
			&& !params.node.isRowPinned()
			&& getLevelByDepth(
				SPOTIFY_SUPERGRID_CONFIG,
				params.context.gridLevelDepth,
			) === SpotifyLevel.AD_ACCOUNT,
	'bg-max-level': (params: RowClassParams) => params.node.level <= params.context.gridLevelDepth
			&& params.node.level > 0,
	'bg-level-collapsed': (params: RowClassParams) => params.node.level < params.context.gridLevelDepth
			&& params.node.level < (getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, SpotifyLevel.GROUP))
            && params.data?.level !== SpotifyLevel.BREAKDOWN,
	'bg-level-expanded': (params: RowClassParams) => params.node.expanded,

	// 'row-level-ad-account': (params: RowClassParams) => params.data?.level === RedditLevel.AD_ACCOUNT,
	'row-level-campaign':   (params: RowClassParams) => params.data?.level === SpotifyLevel.CAMPAIGN,
	'row-level-group':      (params: RowClassParams) => params.data?.level === SpotifyLevel.GROUP,
	'row-level-ad':         (params: RowClassParams) => params.data?.level === SpotifyLevel.AD,
	'row-top-expand-level': (params: RowClassParams) => (params.rowIndex ?? 0) - (params.node?.parent?.rowIndex ?? 0) === 1
				&& params.node?.level !== 0,
	'row-expanded': (params: RowClassParams) => params.node?.expanded,

	// 'row-level-campaign-start': isRowStartOfNextLevel(RedditLevel.AD_ACCOUNT),
	'row-level-group-start': isRowStartOfNextLevel(SpotifyLevel.CAMPAIGN),
	'row-level-ad-start':    isRowStartOfNextLevel(SpotifyLevel.GROUP),

	'row-level-campaign-end': isRowEndOfLevel(SpotifyLevel.CAMPAIGN),
	'row-level-group-end':    isRowEndOfLevel(SpotifyLevel.GROUP),
	'row-level-ad-end':       isRowEndOfLevel(SpotifyLevel.AD),

	'row-level-breakdown': (params: RowClassParams) => params.data?.level === SpotifyLevel.BREAKDOWN
    || params.data?.isBreakdownParent,
});

export const mapSortField = (gridField: string, level: SpotifyLevel) => {
	switch (true) {
		// Campaign fields
		case gridField === 'campaignFirst':
			return 'campaignId';
		case gridField === 'status':
			if (level === SpotifyLevel.CAMPAIGN) return 'campaignStatus';
			if (level === SpotifyLevel.GROUP) return 'groupStatus';
			if (level === SpotifyLevel.AD) return 'adStatus';

			break;

		// Group fields
		case gridField === 'groupFirst':
			return 'groupId';

		// Ad fields
		case gridField === 'adFirst':
			return 'adId';

		case gridField === 'creativeText':
			return 'textValue';

		case gridField.includes('creativeContentCaption'):
			return 'creativeContentCaption';

		case gridField.includes('creativeContentMediaUrl'):
			return 'creativeContentMediaUrl';
	}

	return gridField;
};

export const filterSortField = (gridField: string, level: SpotifyLevel) => {
	// if (Object.values(StatsOrderByColumn).includes(gridField as StatsOrderByColumn)) {
	// 	return true;
	// }

	switch (level) {
		case SpotifyLevel.AD:
			return Object.values(AdOrderByColumn).includes(gridField as AdOrderByColumn);

		case SpotifyLevel.GROUP:
			return Object.values(GroupOrderByColumn).includes(gridField as GroupOrderByColumn);

		case SpotifyLevel.CAMPAIGN:
			return Object.values(CampaignOrderByColumn).includes(gridField as CampaignOrderByColumn);

		case SpotifyLevel.AD_ACCOUNT:
			return Object.values(AdAccountOrderByColumns).includes(gridField as AdAccountOrderByColumns);
	}

	return false;
};

type IsActionCellParams = {
	colDef?: TransformedSuperGridColDef<SpotifyLevel>,
	data?: Record<string, any>
};

// A cell is a action cell when pressing enter on it will trigger an action
export function isActionCell({ colDef, data }: IsActionCellParams) {
	if (
		colDef?.elasticParent
        || colDef?.colId === 'expand'
        || colDef?.field === 'creativeAllowComments'
        || colDef?.field === 'index'
        || data?.field === 'creativeAllowComments'
        || colDef?.field === 'clientPinnedSettings'
        || data?.supergridIsCreateRow
        || data?.isCreate
	) {
		return true;
	}

	return false;
}
